import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

const RECEIVED_TYPING_TIMEOUT_MS = 3000;
const SEND_THROTTLE_MS = 1200;

const getPairKey = (userA: string, userB: string) => [userA, userB].sort().join(':');

export function useDmTyping(selfId?: string | null, otherId?: string | null) {
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const resetTimerRef = useRef<number | null>(null);
  const lastSentTypingRef = useRef(false);
  const lastSentAtRef = useRef(0);

  const pairKey = useMemo(() => {
    if (!selfId || !otherId || selfId === otherId) {
      return null;
    }

    return getPairKey(selfId, otherId);
  }, [otherId, selfId]);

  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const setSelfTyping = useCallback(
    async (isTyping: boolean) => {
      if (!selfId || !otherId || selfId === otherId || !channelRef.current) {
        return;
      }

      const now = Date.now();
      const shouldSkipTrueBroadcast =
        isTyping &&
        lastSentTypingRef.current &&
        now - lastSentAtRef.current < SEND_THROTTLE_MS;

      if (shouldSkipTrueBroadcast || lastSentTypingRef.current === isTyping) {
        return;
      }

      lastSentTypingRef.current = isTyping;
      lastSentAtRef.current = now;

      await channelRef.current.send({
        type: 'broadcast',
        event: 'typing',
        payload: {
          user_id: selfId,
          isTyping,
        },
      });
    },
    [otherId, selfId]
  );

  useEffect(() => {
    if (!pairKey || !selfId || !otherId) {
      setIsOtherTyping(false);
      return;
    }

    const channel = supabase.channel(`dm-typing:${pairKey}`);
    channelRef.current = channel;

    channel
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (!payload || payload.user_id !== otherId) {
          return;
        }

        const otherIsTyping = Boolean(payload.isTyping);
        setIsOtherTyping(otherIsTyping);
        clearResetTimer();

        if (otherIsTyping) {
          resetTimerRef.current = window.setTimeout(() => {
            setIsOtherTyping(false);
            resetTimerRef.current = null;
          }, RECEIVED_TYPING_TIMEOUT_MS);
        }
      })
      .subscribe();

    return () => {
      if (lastSentTypingRef.current) {
        channel.send({
          type: 'broadcast',
          event: 'typing',
          payload: {
            user_id: selfId,
            isTyping: false,
          },
        });
      }

      clearResetTimer();
      lastSentTypingRef.current = false;
      lastSentAtRef.current = 0;
      channelRef.current = null;
      setIsOtherTyping(false);
      supabase.removeChannel(channel);
    };
  }, [clearResetTimer, otherId, pairKey, selfId]);

  return {
    isOtherTyping,
    setSelfTyping,
  };
}
